import * as Sentry from '@sentry/react';

import { describeThrown } from './capturable-error';
import { FrontendError } from './frontend-error';

export const FRONTEND_ERROR_BUFFER_LIMIT = 50;

export type FrontendErrorEntry = {
  id: string;
  code: string;
  cause?: unknown;
  capturedAt: number;
  meta?: Record<string, unknown>;
};

const buffer: FrontendErrorEntry[] = [];

export function captureFrontendError(err: unknown, origin: string, meta?: Record<string, unknown>): string {
  const fe = err instanceof FrontendError ? err : new FrontendError({ code: 'errors.generic.unexpected', cause: err });

  const entry: FrontendErrorEntry = {
    id: fe.id,
    code: fe.code,
    capturedAt: fe.capturedAt,
    meta: { origin, ...meta },
  };

  if (fe.cause !== undefined) entry.cause = fe.cause;

  buffer.push(entry);

  if (buffer.length > FRONTEND_ERROR_BUFFER_LIMIT) buffer.shift();

  Sentry.withScope((scope) => {
    scope.setTag('error.id', fe.id);
    scope.setTag('error.code', fe.code);
    scope.setContext('frontend', { origin, ...(meta ?? {}) });
    Sentry.captureException(capturableOf(fe, origin));
  });

  return fe.id;
}

function capturableOf(fe: FrontendError, origin: string): Error {
  if (fe.cause instanceof Error) return fe.cause;

  if (fe.cause === undefined) return new Error(`${fe.code} (${origin})`);

  return new Error(`${fe.code} (${origin}): ${describeThrown(fe.cause)}`, { cause: fe.cause });
}

export function readFrontendErrorBuffer(): readonly FrontendErrorEntry[] {
  return buffer;
}

export function clearFrontendErrorBuffer(): void {
  buffer.length = 0;
}
